import { Component, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, ParamMap, Params, Router } from '@angular/router';

import { BayclubsAlertService } from '@bayclubs/alert';

import { camps } from '../../models/models.generated';
import { RosterApiService } from '../../services/api/roster-api.service';
import { RosterEventService } from './roster-event.service';
import { RosterFilter } from '../../models/filters/roster-filter.model';
import { FiltersHelperService } from '../../services/filters-helper.service';
import { TimeService } from '@bayclubs/utils';

@Component({
    selector: 'app-roster',
    host: { 'data-testid': 'roster-page' },
    templateUrl: './roster.component.html',
    styleUrls: ['./roster.component.scss'],
    standalone: false
})
export class RosterComponent implements OnInit {
    public searchForm: UntypedFormGroup;

    public items: camps.RosterInfoItem[] = [];
    public isBusy: boolean = false;

    private onSearchSubscription;

    public filters: RosterFilter;

    constructor(
        private formBuilder: UntypedFormBuilder,
        private rosterApi: RosterApiService,
        private alert: BayclubsAlertService,
        private rosterEvent: RosterEventService,
        private filtersHelperService: FiltersHelperService,
        private time: TimeService
    ) {
        this.filters = this.getFilter();
    }

    ngOnInit() {
        this.initForm(this.filters);
    }

    ngOnDestroy() {
        if (this.onSearchSubscription) {
            this.onSearchSubscription.unsubscribe();
            this.onSearchSubscription = null;
        }
    }

    private initForm(filter: RosterFilter): void {
        this.searchForm = this.formBuilder.group({
            clubId: [filter.clubId, [Validators.required]],
            dateFrom: [filter.dateFrom, [Validators.required]],
            dateTo: [filter.dateTo, [Validators.required]],
            clubCode: [filter.clubCode, [Validators.required]],
        });

        this.onSearchSubscription = this.rosterEvent.search.subscribe(() => {
            this.onSearch();
        });
    }

    public onSearch() {
        let request = this.buildRequest();
        this.isBusy = true;

        this.rosterApi.search(request).subscribe(
            x => {
                this.items = x.value.items;
            },
            error => {
                this.alert.showError(error);
            },
            () => {
                const filter: RosterFilter = <RosterFilter>{
                    clubId: this.searchForm.controls['clubId'].value,
                    dateFrom: this.searchForm.controls['dateFrom'].value,
                    dateTo: this.searchForm.controls['dateTo'].value,
                    clubCode: this.searchForm.controls['clubCode'].value,
                };

                this.filtersHelperService.mergeQueryParam(filter, 'Roster');

                this.isBusy = false;
            }
        );
    }

    public get noItems() {
        return this.items == null || this.items.length == 0;
    }

    public getProfile() {
        let request = this.buildRequest();

        this.rosterApi.getProfile(request, this.clubCode);
    }

    public getSummary() {
        let request = this.buildRequest();

        this.rosterApi.getSummary(request, this.clubCode);
    }

    private buildRequest(): camps.RosterRequest {
        return <camps.RosterRequest>{
            clubId: this.searchForm.controls['clubId'].value,
            dateFrom: this.searchForm.controls['dateFrom'].value,
            dateTo: this.searchForm.controls['dateTo'].value,
            clubCode: this.searchForm.controls['clubCode'].value,
        };
    }

    private get clubCode(): string {
        return this.searchForm.controls['clubCode'].value;
    }

    private getFilter() {
        const nowMoment = this.time.nowInTimeZone(this.time.defaultTimezone());
        const nowBcDate = this.time.toBcDate(nowMoment);

        const defaultFilter: RosterFilter = {
            clubId: null,
            dateFrom: nowBcDate,
            dateTo: nowBcDate,
            clubCode: null
        };

        const restoredFilter = this.filtersHelperService.restoreFilter<RosterFilter>('Roster');

        return restoredFilter ? restoredFilter : defaultFilter;
    }
}
